import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Input, Select, DatePicker, message, Upload, Progress, Spin, Form, Tabs } from 'antd';
import { UploadOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const { Option } = Select;
const { RangePicker } = DatePicker;

const MigrateSMSSummaryPage = () => {
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentJob, setCurrentJob] = useState(null);
  const [activeTab, setActiveTab] = useState('jobs');
  
  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [selectedUserFilter, setSelectedUserFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  
  const [form] = Form.useForm();

  // Fetch migrated SMS summary data
  const fetchSMSSummaryData = async () => {
    setCurrentPage(1);
    await fetchSMSSummaryDataWithPagination(1, pageSize);
  };

  // Fetch migrated SMS summary data with pagination
  const fetchSMSSummaryDataWithPagination = async (page = 1, limit = 50) => {
    if (!selectedUser && !selectedUserFilter) {
      message.warning('Please select a user first');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // If using filter dropdown, send user_name, otherwise send user_id
      if (selectedUserFilter) {
        const selectedUserObj = users.find(u => u.id === selectedUserFilter);
        if (selectedUserObj) {
          params.append('user_name', selectedUserObj.username);
        }
      } else {
        params.append('user_id', selectedUser);
      }
      
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (dateRange && dateRange.length === 2) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
        params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
      }
      
      const url = getApiEndpoint(`/migration/sms-summary/data?${params}`);
      console.log('Fetching SMS summary data from:', url);
      
      const response = await authFetch(url);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('API response:', result);
        setData(result.data || []);
        setTotalData(result.total || 0);
        setCurrentPage(page);
        setPageSize(limit);
        console.log('Data set:', result.data?.length || 0, 'records');
        console.log('Total count:', result.total || 0, 'records');
      } else {
        console.error('Failed to fetch data:', response.statusText);
        message.error('Failed to fetch SMS summary data');
      }
    } catch (error) {
      console.error('Error fetching SMS summary data:', error);
      message.error('Error fetching SMS summary data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch migration jobs
  const fetchJobs = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/sms-summary/jobs'));
      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      }
    } catch (error) {
      message.error('Failed to fetch jobs');
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!selectedUser) {
      message.error('Please select a user before uploading');
      return false;
    }

    setUploadLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', selectedUser);

    try {
      const xhr = new XMLHttpRequest();
      
      // Progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      // Create promise for XHR
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error('Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', getApiEndpoint('/migration/sms-summary/upload'));
      const token = localStorage.getItem('token');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);

      const result = await uploadPromise;
      
      message.success(`File uploaded successfully! Job ID: ${result.job_id}`);
      setUploadProgress(0);
      fetchJobs();
      
      // Start polling for job status
      startPolling();
      
    } catch (error) {
      message.error('Failed to upload file');
      setUploadProgress(0);
    } finally {
      setUploadLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Start/pause/stop job
  const controlJob = async (jobId, action) => {
    try {
      const response = await authFetch(getApiEndpoint(`/migration/sms-summary/jobs/${jobId}/${action}`), {
        method: 'POST'
      });
      
      if (response.ok) {
        message.success(`Job ${action} successfully`);
        fetchJobs();
      } else {
        message.error(`Failed to ${action} job`);
      }
    } catch (error) {
      message.error(`Error ${action} job`);
    }
  };

  // Poll for job updates
  const startPolling = () => {
    if (pollingInterval) clearInterval(pollingInterval);
    
    const interval = setInterval(() => {
      fetchJobs();
    }, 3000);
    
    setPollingInterval(interval);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Fetch users for dropdown
  const [users, setUsers] = useState([]);
  const fetchUsers = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/users'));
      if (response.ok) {
        const responseData = await response.json();
        setUsers(responseData.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchJobs();
    
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    if (selectedUser || selectedUserFilter) {
      setCurrentPage(1);
      fetchSMSSummaryData();
    }
  }, [selectedUserFilter, dateRange]);

  // Job status columns
  const jobColumns = [
    {
      title: 'Job ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: 'Filename',
      dataIndex: 'source_filename',
      key: 'source_filename',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = {
          'CREATED': 'blue',
          'READY': 'cyan',
          'RUNNING': 'processing',
          'PAUSED': 'warning',
          'COMPLETED': 'success',
          'PARTIALLY_COMPLETED': 'orange',
          'FAILED': 'error',
          'PARTIAL': 'orange'
        }[status] || 'default';
        
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Total Rows',
      dataIndex: 'total_rows',
      key: 'total_rows',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'Success Rows',
      dataIndex: 'success_rows',
      key: 'success_rows',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'Failed Rows',
      dataIndex: 'failed_rows',
      key: 'failed_rows',
      render: (value) => {
        const num = value || 0;
        return <span style={{color: '#ff4d4f'}}>{formatIndianNumber(num)}</span>;
      },
      align: 'right'
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const total = record.total_rows || 0;
        const processed = record.processed_rows || 0;
        const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
        
        return (
          <Progress 
            percent={percent} 
            size="small"
            status={record.status === 'FAILED' ? 'exception' : 'active'}
          />
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const canStart = ['CREATED', 'READY', 'PAUSED'].includes(record.status);
        const canPause = record.status === 'RUNNING';
        const canStop = ['RUNNING', 'PAUSED'].includes(record.status);
        
        return (
          <Space orientation="horizontal">
            {canStart && (
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                size="small"
                onClick={() => controlJob(record.id, 'start')}
              >
                Start
              </Button>
            )}
            {canPause && (
              <Button 
                icon={<PauseCircleOutlined />}
                size="small"
                onClick={() => controlJob(record.id, 'pause')}
              >
                Pause
              </Button>
            )}
            {canStop && (
              <Button 
                danger 
                icon={<StopOutlined />}
                size="small"
                onClick={() => controlJob(record.id, 'stop')}
              >
                Stop
              </Button>
            )}
          </Space>
        );
      }
    }
  ];

  // Data columns
  const dataColumns = [
    {
      title: 'User Name',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Report Date',
      dataIndex: 'report_date',
      key: 'report_date',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'SMS Unit',
      dataIndex: 'sms_unit',
      key: 'sms_unit',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'SMS Count',
      dataIndex: 'sms_count',
      key: 'sms_count',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'Sent',
      dataIndex: 'sent_count',
      key: 'sent_count',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered_count',
      key: 'delivered_count',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    },
    {
      title: 'Failed',
      dataIndex: 'failed_count',
      key: 'failed_count',
      render: (value) => formatIndianNumber(value),
      align: 'right'
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="SMS Summary Migration" style={{ marginBottom: '16px' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          {/* Upload Section */}
          <Card size="small" title="Upload File">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <div>
                <label style={{ marginRight: '8px' }}>Select User: </label>
                <Select
                  style={{ width: 300 }}
                  placeholder="Select a user"
                  value={selectedUser}
                  onChange={setSelectedUser}
                >
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.username}
                    </Option>
                  ))}
                </Select>
              </div>
              
              <Upload
                accept=".csv,.xls,.xlsx"
                beforeUpload={handleFileUpload}
                showUploadList={false}
                disabled={!selectedUser || uploadLoading}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploadLoading}
                  disabled={!selectedUser}
                >
                  Upload SMS Summary File
                </Button>
              </Upload>
              
              {uploadProgress > 0 && (
                <Progress percent={uploadProgress} />
              )}
            </Space>
          </Card>

          {/* Tabs for Jobs and Data */}
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: 'jobs',
                label: 'Migration Jobs',
                children: (
                  <Table
                    columns={jobColumns}
                    dataSource={Array.isArray(jobs) ? jobs : []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                )
              },
              {
                key: 'data',
                label: 'Migrated Data',
                children: (
                  <>
                    <Space style={{ marginBottom: '16px' }}>
                      <Select
                        showSearch
                        placeholder="Search by user name"
                        style={{ width: 200 }}
                        value={selectedUserFilter}
                        onChange={setSelectedUserFilter}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                      >
                        {users.map(user => (
                          <Option key={user.id} value={user.id}>
                            {user.username}
                          </Option>
                        ))}
                      </Select>
                      <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        placeholder={['Start Date', 'End Date']}
                      />
                      <Button 
                        type="primary" 
                        onClick={fetchSMSSummaryData}
                        loading={loading}
                        disabled={!selectedUser && !selectedUserFilter}
                      >
                        Search
                      </Button>
                      <Button icon={<ReloadOutlined />} onClick={fetchSMSSummaryData}>
                        Refresh
                      </Button>
                    </Space>
                    
                    <Table
                      columns={dataColumns}
                      dataSource={Array.isArray(data) ? data : []}
                      rowKey="id"
                      loading={loading}
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalData,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                        onChange: (page, newPageSize) => {
                          fetchSMSSummaryDataWithPagination(page, newPageSize);
                        },
                        onShowSizeChange: (current, size) => {
                          fetchSMSSummaryDataWithPagination(1, size);
                        }
                      }}
                    />
                  </>
                )
              }
            ]}
          />
        </Space>
      </Card>
    </div>
  );
};

export default MigrateSMSSummaryPage;
